import type { ValueColorStop } from '../../../../shared/charts/types';

/** Reference scale the user described (0°C cool, 40°C hot). */
export const TEMPERATURE_HEAT_GRADIENT_MIN_C = 0;
export const TEMPERATURE_HEAT_GRADIENT_MAX_C = 40;

/**
 * Legacy extended Y-axis bounds — used only for empty-data fallbacks and colour
 * clamping in `getTemperatureHeatColor`.
 */
export const TEMPERATURE_HEAT_Y_MIN_C = -25;
export const TEMPERATURE_HEAT_Y_MAX_C = 65;

export interface TemperatureHeatColourStop {
  value: number;
  rgb: readonly [number, number, number];
}

/** @deprecated Use `TEMPERATURE_HEAT_Y_MIN_C` — kept for existing imports. */
export const TEMPERATURE_HEAT_COLOR_MIN_C = TEMPERATURE_HEAT_Y_MIN_C;

/** @deprecated Use `TEMPERATURE_HEAT_Y_MAX_C` — kept for existing imports. */
export const TEMPERATURE_HEAT_COLOR_MAX_C = TEMPERATURE_HEAT_Y_MAX_C;

/**
 * Colour stops shared with the 7-day outlook temperature bars (in Celsius).
 * ≤5°C = light blue, ~17°C = yellow, ~30°C = orange, ≥35°C = red.
 */
export const TEMPERATURE_HEAT_COLOUR_STOPS_C: TemperatureHeatColourStop[] = [
  { value: 5, rgb: [35, 132, 211] }, // #2384d3
  { value: 17, rgb: [255, 171, 0] }, // #ffab00
  { value: 30, rgb: [255, 92, 57] }, // #ff5c39
  { value: 35, rgb: [168, 0, 0] }, // #a80000
];

/** Equivalent stops in Fahrenheit — same hues as `TEMPERATURE_HEAT_COLOUR_STOPS_C`. */
export const TEMPERATURE_HEAT_COLOUR_STOPS_F: TemperatureHeatColourStop[] = [
  { value: 41, rgb: [35, 132, 211] },
  { value: 63, rgb: [255, 171, 0] },
  { value: 86, rgb: [255, 92, 57] },
  { value: 95, rgb: [168, 0, 0] },
];

const interpolateRgb = (
  from: readonly [number, number, number],
  to: readonly [number, number, number],
  ratio: number,
): string => {
  const red = Math.round(from[0] + ratio * (to[0] - from[0]));
  const green = Math.round(from[1] + ratio * (to[1] - from[1]));
  const blue = Math.round(from[2] + ratio * (to[2] - from[2]));
  return `rgb(${red}, ${green}, ${blue})`;
};

/** Interpolate an RGB colour for a temperature against the configured stops. */
export const getTemperatureHeatColorForStops = (
  temp: number,
  stops: TemperatureHeatColourStop[],
): string => {
  if (stops.length === 0) {
    return 'currentColor';
  }

  if (temp <= stops[0].value) {
    const [r, g, b] = stops[0].rgb;
    return `rgb(${r}, ${g}, ${b})`;
  }

  const lastStop = stops[stops.length - 1];
  if (temp >= lastStop.value) {
    const [r, g, b] = lastStop.rgb;
    return `rgb(${r}, ${g}, ${b})`;
  }

  for (let index = 0; index < stops.length - 1; index += 1) {
    const from = stops[index];
    const to = stops[index + 1];

    if (temp >= from.value && temp <= to.value) {
      const span = to.value - from.value;
      const ratio = span === 0 ? 0 : (temp - from.value) / span;
      return interpolateRgb(from.rgb, to.rgb, ratio);
    }
  }

  const [r, g, b] = lastStop.rgb;
  return `rgb(${r}, ${g}, ${b})`;
};

/** Blue→yellow→orange→red for a temperature in °C (matches 7-day outlook). */
export const getTemperatureHeatColor = (celsius: number): string =>
  getTemperatureHeatColorForStops(celsius, TEMPERATURE_HEAT_COLOUR_STOPS_C);

/** Figma temperature chart fill opacity (`paint0_linear_9336_1477`). */
export const TEMPERATURE_CHART_FILL_OPACITY = 0.4;

/** Chart fill palette: blue → tan → red (plot bottom → top). */
export const TEMPERATURE_CHART_FILL_RGB_STOPS: readonly (readonly [
  number,
  number,
  number,
])[] = [
  [180, 220, 255], // #B4DCFF
  [179, 153, 115], // #B39973
  [178, 10, 10], // #B20A0A
] as const;

export const TEMPERATURE_CHART_FILL_GRADIENT_OFFSETS = [
  '0%',
  '50%',
  '100%',
] as const;

const toRgba = (
  rgb: readonly [number, number, number],
  opacity: number,
): string => `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${opacity})`;

const TEMPERATURE_LINE_DARKEN = 0.55;

const darkenRgbTuple = (
  rgb: readonly [number, number, number],
  amount: number,
): readonly [number, number, number] => [
  Math.round(rgb[0] * (1 - amount)),
  Math.round(rgb[1] * (1 - amount)),
  Math.round(rgb[2] * (1 - amount)),
];

const interpolatePaletteRatio = (ratio: number): string => {
  const clamped = Math.max(0, Math.min(1, ratio));

  if (clamped <= 0.5) {
    return interpolateRgb(
      TEMPERATURE_CHART_FILL_RGB_STOPS[0],
      TEMPERATURE_CHART_FILL_RGB_STOPS[1],
      clamped / 0.5,
    );
  }

  return interpolateRgb(
    TEMPERATURE_CHART_FILL_RGB_STOPS[1],
    TEMPERATURE_CHART_FILL_RGB_STOPS[2],
    (clamped - 0.5) / 0.5,
  );
};

const interpolatePaletteRatioLine = (ratio: number): string => {
  const clamped = Math.max(0, Math.min(1, ratio));

  if (clamped <= 0.5) {
    return interpolateRgb(
      darkenRgbTuple(
        TEMPERATURE_CHART_FILL_RGB_STOPS[0],
        TEMPERATURE_LINE_DARKEN,
      ),
      darkenRgbTuple(
        TEMPERATURE_CHART_FILL_RGB_STOPS[1],
        TEMPERATURE_LINE_DARKEN,
      ),
      clamped / 0.5,
    );
  }

  return interpolateRgb(
    darkenRgbTuple(
      TEMPERATURE_CHART_FILL_RGB_STOPS[1],
      TEMPERATURE_LINE_DARKEN,
    ),
    darkenRgbTuple(
      TEMPERATURE_CHART_FILL_RGB_STOPS[2],
      TEMPERATURE_LINE_DARKEN,
    ),
    (clamped - 0.5) / 0.5,
  );
};

/** Map a temperature within the day's Y domain to the chart palette. */
export const getTemperatureChartColor = (
  temperature: number,
  yMin: number,
  yMax: number,
): string => {
  const span = yMax - yMin;
  const ratio = span === 0 ? 0 : (temperature - yMin) / span;
  return interpolatePaletteRatio(ratio);
};

export const getTemperatureChartLineColor = (
  temperature: number,
  yMin: number,
  yMax: number,
): string => {
  const span = yMax - yMin;
  const ratio = span === 0 ? 0 : (temperature - yMin) / span;
  return interpolatePaletteRatioLine(ratio);
};

export interface TemperatureFillGradientStop {
  offset: string;
  color: string;
}

/** Area fill matching Figma `paint0_linear_9336_1477`. */
export const buildTemperatureFillGradientStops =
  (): TemperatureFillGradientStop[] =>
    TEMPERATURE_CHART_FILL_RGB_STOPS.map((rgb, index) => ({
      offset: TEMPERATURE_CHART_FILL_GRADIENT_OFFSETS[index],
      color: toRgba(rgb, TEMPERATURE_CHART_FILL_OPACITY),
    }));

/** Line stroke gradient using darkened palette colours. */
export const buildTemperatureLineGradientStops =
  (): TemperatureFillGradientStop[] =>
    TEMPERATURE_CHART_FILL_RGB_STOPS.map((rgb, index) => ({
      offset: TEMPERATURE_CHART_FILL_GRADIENT_OFFSETS[index],
      color: (() => {
        const [r, g, b] = darkenRgbTuple(rgb, TEMPERATURE_LINE_DARKEN);
        return `rgb(${r}, ${g}, ${b})`;
      })(),
    }));

export interface TemperatureHeatValueGradientConfig {
  stops: ValueColorStop[];
  applyToLine: boolean;
  fillOpacity: number;
}

/**
 * Two-stop chart gradient using the same endpoint colours as the 7-day outlook
 * bars (`getTemperatureHeatColorForStops` at min and max temperature).
 */
export const buildTemperatureHeatValueGradient = (
  minTemperature: number,
  maxTemperature: number,
  colourStops: TemperatureHeatColourStop[] = TEMPERATURE_HEAT_COLOUR_STOPS_C,
): TemperatureHeatValueGradientConfig => ({
  stops: [
    {
      value: minTemperature,
      color: getTemperatureHeatColorForStops(minTemperature, colourStops),
    },
    {
      value: maxTemperature,
      color: getTemperatureHeatColorForStops(maxTemperature, colourStops),
    },
  ],
  applyToLine: true,
  fillOpacity: 0.85,
});

/** Y-axis domain fitted to the day's temperatures (min at plot bottom). */
export const getTemperatureHeatYDomain = (
  values: number[],
): [number, number] => {
  if (values.length === 0) {
    return [TEMPERATURE_HEAT_GRADIENT_MIN_C, TEMPERATURE_HEAT_Y_MAX_C];
  }

  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);

  if (dataMin === dataMax) {
    return [dataMin - 1, dataMax + 1];
  }

  return [dataMin, dataMax];
};
